import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import IllustrationImage from '#client/components/CardModal/components/IllustrationImage';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import { RELEASE_BANKING_UPSELL_PAGE_AND_NON_QUALIFIED_PAGE_TO_INCLUDE_BLUEVINE as OFFER_FLAG } from '#client/constants/experimentsFlags';
import {
  validationMessagesMappingWithBoAAndBluevine,
  validationMessagesMappingWithChase,
  validationMessagesMappingWithChaseAndBluevine
} from '#client/constants/validationMessages';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import { getValidationMessage } from '#client/utils/validateMessage';

import BoAAndBluevineOffers from './components/BoAAndBluevineOffers';
import ChaseAndBluevineOffers from './components/ChaseAndBluevineOffers';
import ChaseOffer from './components/ChaseOffer';
import {
  QualifiedOuterBoxStyled,
  Wrapper,
  HeadingStyled
} from './components/ValidationMessage.styles';

import { REASONS_HIDE_OFFERS } from './constants/reasons';

const ValidationMessage = ({ validationStatus = 'default' }) => {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const offerVariation = useExperimentFlag(OFFER_FLAG.FLAG, OFFER_FLAG.VARIATIONS.CONTROL);

  const variationsToShowBoAAndBluevineOffer = [
    OFFER_FLAG.VARIATIONS.VERSION_A,
    OFFER_FLAG.VARIATIONS.VERSION_B
  ];
  const variationsToShowChaseOffer = [OFFER_FLAG.VARIATIONS.CONTROL];

  const chaseAndBluevineVariations = [OFFER_FLAG.VARIATIONS.VERSION_C];

  const showBoAAndBluevineOffers =
    variationsToShowBoAAndBluevineOffer.includes(offerVariation) &&
    !REASONS_HIDE_OFFERS.includes(validationStatus);
  const showChaseOffer =
    variationsToShowChaseOffer.includes(offerVariation) &&
    !REASONS_HIDE_OFFERS.includes(validationStatus);

  const showChaseAndBluevineOffers =
    chaseAndBluevineVariations.includes(offerVariation) &&
    !REASONS_HIDE_OFFERS.includes(validationStatus);

  const validationMessagesMapping = (() => {
    if (chaseAndBluevineVariations.includes(offerVariation)) {
      return validationMessagesMappingWithChaseAndBluevine;
    }

    if (variationsToShowChaseOffer.includes(offerVariation)) {
      return validationMessagesMappingWithChase;
    }

    // Default case
    return validationMessagesMappingWithBoAAndBluevine;
  })();

  const { header, body, url, modalName } = getValidationMessage(
    validationStatus,
    validationMessagesMapping
  );

  /**
   * Tracking Details: Tracking added for the Banking Application Not Eligible screen
   * Question: What % of traffic is coming from each source pointing to this page?
   */
  useModalDisplayTracker({
    elementName: modalName,
    elementText: modalName,
    appPageName: 'banking_upsell_dashboard'
  });

  const Content = (
    <FocusGrid>
      <IllustrationImage />
      <Wrapper>
        <Stack>
          <HeadingStyled
            size="2xl"
            weight="bold"
          >
            {header}
          </HeadingStyled>
          <Text
            color="secondary"
            size="md"
          >
            {body}
          </Text>
        </Stack>
        {showChaseOffer && <ChaseOffer hyperLink={`${url}&u1=${businessEntityUuid}`} />}
        {showBoAAndBluevineOffers && <BoAAndBluevineOffers clickstreamInfo={{ modalName }} />}
        {showChaseAndBluevineOffers && <ChaseAndBluevineOffers clickstreamInfo={{ modalName }} />}
      </Wrapper>
    </FocusGrid>
  );

  return (
    <QualifiedOuterBoxStyled>
      <ContainerGrid>{Content}</ContainerGrid>
    </QualifiedOuterBoxStyled>
  );
};

ValidationMessage.propTypes = {
  validationStatus: PropTypes.string
};

export default ValidationMessage;
